import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { MapPin, ImageOff, ScanLine } from "lucide-react";
import { api, errCode } from "../lib/api";
import { useLang } from "../lib/i18n";
import { useAuth } from "../lib/auth";
import { fmtDateTime } from "../lib/time";
import { StatusBadge } from "../components/Badges";
import { AuthImage, Inp } from "../components/Common";
import { WeightPanel, PhotoPanel, FieldActions, DockPanel, DockSummary, CorrectionPanel, PhPanel, PhSummary, useAct } from "../components/BinPanels";
import { ScanError } from "./Scan";
import { CloudOff } from "lucide-react";
import { isNetErr, getLocalBin, saveLocalBin, getCachedBin, cacheBin, offlineClient, getBundle } from "../lib/offline";
import { useSyncState } from "../components/SyncBadge";

const LocalImage = ({ blob }) => {
  const [url, setUrl] = useState(null);
  useEffect(() => { const u = URL.createObjectURL(blob); setUrl(u); return () => URL.revokeObjectURL(u); }, [blob]);
  return url ? <img src={url} alt="bin" className="w-full max-h-96 object-contain rounded-xl bg-slate-100" data-testid="bin-photo-local" /> : null;
};

const FIELD = ["admin", "farm_manager", "supervisor"];
const DOCK = ["admin", "exporter", "farm_manager"];
const PH = ["admin", "exporter", "packing_house"];

const Info = ({ k, v, testid }) => (
  <div><p className="eyebrow">{k}</p><p className="font-semibold" data-testid={testid}>{v || "—"}</p></div>
);

const GpsApprove = ({ bin, setBin }) => {
  const { t } = useLang();
  const { run, busy } = useAct(setBin);
  const [r, setR] = useState("");
  return (
    <div className="card p-4 border-amber-400 space-y-2" data-testid="gps-approve-panel">
      <p className="font-bold text-amber-900">{t("gps_pending")}: {bin.gps_exception?.reason}</p>
      <Inp label={t("reason")} value={r} onChange={(e) => setR(e.target.value)} testid="gps-approve-reason-input" />
      <button className="btn-secondary w-full" disabled={busy || r.trim().length < 3} onClick={() => run(() => api.post(`/bins/${bin.id}/gps-exception/approve`, { reason: r }))} data-testid="gps-approve-button">{t("approve_gps")}</button>
    </div>
  );
};

const History = ({ items, lang, t }) => (
  <section className="card p-4" data-testid="bin-history">
    <ol className="space-y-2">
      {items.map((h) => (
        <li key={h.id} className="text-sm border-l-4 border-slate-300 pl-3">
          <span className="font-bold">{t(`act_${h.action}`)}</span> · {h.by_name} · <span className="text-slate-500">{fmtDateTime(h.at, lang)}</span>
          {h.reason && <span className="block text-slate-700">“{h.reason}”</span>}
        </li>
      ))}
    </ol>
  </section>
);

export default function BinDetail() {
  const { id } = useParams();
  const { t, lang } = useLang();
  const { user } = useAuth();
  const [bin, setBin] = useState(null);
  const [err, setErr] = useState("");
  const [photoKey, setPhotoKey] = useState(0);
  const [mode, setMode] = useState("server");
  const [policy, setPolicy] = useState(null);
  const sync = useSyncState();
  const load = useCallback(async () => {
    const local = await getLocalBin(id);
    if (local) { setBin(local); setMode("local"); return; }
    try {
      const r = await api.get(`/bins/${id}`);
      setBin(r.data); setMode("server"); setErr("");
      if (FIELD.includes(user.role)) cacheBin(r.data);
    } catch (e) {
      if (!isNetErr(e)) return setErr(errCode(e));
      const c = await getCachedBin(id);
      if (!c) return setErr("offline_bin_unavailable");
      setBin(c); setMode("cached");
    }
  }, [id, user.role]);
  useEffect(() => { load(); }, [load, sync.pending]);
  useEffect(() => { if (mode !== "server") getBundle().then((b) => setPolicy(b?.policy)); }, [mode]);
  const setAndReload = useCallback((fn) => { setBin(fn); load(); }, [load]);

  if (err) return <div className="max-w-md mx-auto"><ScanError err={err} /></div>;
  if (!bin) return <p>{t("loading")}</p>;
  const n = bin.names || {};
  const offline = mode !== "server";
  const field = FIELD.includes(user.role) && ["active", "ready"].includes(bin.status);
  const dock = DOCK.includes(user.role) && !offline;
  const http = offline ? { post: async (...a) => { if (!(await getLocalBin(bin.id))) await saveLocalBin(bin); return offlineClient(bin.id, policy).post(...a); } } : api;

  return (
    <div className="max-w-5xl mx-auto grid lg:grid-cols-5 gap-5" data-testid="bin-detail-page">
      {offline && <div className="lg:col-span-5 flex items-center gap-2 p-3 rounded-xl bg-amber-100 border-2 border-amber-400 font-semibold text-amber-900" data-testid="offline-bin-banner">
        <CloudOff size={20} />{mode === "local" ? t("offline_pending_bin") : t("offline_cached_bin")}</div>}
      <div className="lg:col-span-3 space-y-4">
        <section className="card p-4 space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <p className="font-mono text-3xl font-bold" data-testid="bin-label-number">{bin.label_number}</p>
            <StatusBadge status={bin.status} />
            {bin.is_test && <span className="text-[10px] font-bold bg-amber-300 px-2 py-0.5 rounded">{t("test_data")}</span>}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <Info k={t("farm")} v={n.farm} /><Info k={t("sector")} v={n.sector} testid="bin-sector" /><Info k={t("variety")} v={n.variety} />
            <Info k={t("contractor")} v={n.contractor} /><Info k={t("crew")} v={n.crew} /><Info k={t("harvest_date")} v={bin.harvest_date} />
            <Info k={t("activated_by")} v={`${bin.activated_by_name} · ${fmtDateTime(bin.activated_at, lang)}`} />
            <Info k="CSG" v={n.csg} />
            <div><p className="eyebrow">{t("gps")}</p>
              {bin.gps ? <p className="font-mono text-sm flex gap-1 items-center" data-testid="bin-gps"><MapPin size={14} />{Number(bin.gps.lat).toFixed(5)}, {Number(bin.gps.lng).toFixed(5)} ±{bin.gps.accuracy}m</p>
                : <p className="text-sm font-bold text-amber-800" data-testid="bin-gps">{t(`gps_${bin.gps_status}`)}</p>}</div>
          </div>
          {bin.notes && <p className="text-sm bg-slate-50 p-2 rounded">{bin.notes}</p>}
          {bin.dispatch && <p className="text-sm font-semibold text-teal-900 bg-teal-50 p-2 rounded" data-testid="bin-dispatch-info">{t("dp_dispatch_info")} {bin.dispatch.number} · {bin.dispatch.driver_name} · {bin.dispatch.truck_plate} · {fmtDateTime(bin.dispatch.at, lang)}</p>}
          {bin.closed && <p className="text-sm font-semibold text-slate-700 bg-slate-100 p-2 rounded" data-testid="bin-closed-info">{t("dp_closed_info")}: {bin.closed.reason} · {bin.closed.by} · {fmtDateTime(bin.closed.at, lang)}</p>}
          {bin.undispatched && !bin.dispatch && <p className="text-sm text-amber-900 bg-amber-50 p-2 rounded" data-testid="bin-undispatched-info">{t("dp_reason_info")} ({bin.undispatched.date}): {bin.undispatched.reason} · {bin.undispatched.by}</p>}
          {bin.discrepancy && <p className="text-sm font-bold text-red-800 bg-red-50 p-2 rounded" data-testid="bin-discrepancy-reason">{t("status_discrepancy")}: {bin.discrepancy.reason}</p>}
        </section>
        <section className="card p-4">
          <p className="eyebrow mb-2">{t("photo")}</p>
          {bin.photo?.blob ? <LocalImage blob={bin.photo.blob} /> : bin.photo ? <>
            <AuthImage key={photoKey} src={`/bins/${bin.id}/photo?size=preview`} alt="bin" className="w-full max-h-96 object-contain rounded-xl bg-slate-100" testid="bin-photo" />
            <p className="text-xs mt-1 text-slate-600">{bin.photo.source === "live" ? t("photo_live") : t("photo_uploaded_flag")} · {fmtDateTime(bin.photo.at, lang)}</p>
          </> : <p className="flex items-center gap-2 text-slate-500"><ImageOff size={18} />—</p>}
        </section>
        {bin.gps_status === "exception_pending" && bin.status !== "closed" && dock && <GpsApprove bin={bin} setBin={setAndReload} />}
        <h2 className="font-bold">{t("history")}</h2>
        <History items={bin.history || []} lang={lang} t={t} />
      </div>
      <div className="lg:col-span-2 space-y-4">
        {(field || bin.field_weight) && <WeightPanel bin={bin} setBin={setAndReload} http={http} />}
        {field && <PhotoPanel bin={bin} setBin={setAndReload} http={http} onUploaded={() => setPhotoKey((k) => k + 1)} />}
        {field && <FieldActions bin={bin} setBin={setAndReload} http={http} />}
        {bin.dock && <DockSummary d={bin.dock} />}
        {dock && <DockPanel bin={bin} setBin={setAndReload} />}
        {bin.ph_receipt && <PhSummary r={bin.ph_receipt} />}
        {PH.includes(user.role) && !offline && <PhPanel bin={bin} setBin={setAndReload} />}
        {dock && bin.status === "verified" && bin.dock && <CorrectionPanel bin={bin} setBin={setAndReload} />}
        <Link to={user.role === "supervisor" ? "/scan" : "/scan?mode=dock"} className="btn-secondary w-full" data-testid="scan-next-button"><ScanLine size={18} />{t("scan_label")}</Link>
      </div>
    </div>
  );
}
